// pages/entry-form/PurchaseInfoPage.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Chip, Dialog,
  DialogContent, DialogActions, CircularProgress, Alert, Tooltip, alpha,
  Fade, Card, Stack, Divider, Paper, Autocomplete, FormHelperText,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, ShoppingCart as PurchaseIcon, Close as CloseIcon,
  CalendarToday as CalendarIcon, Inventory as ProductIcon,
  AttachMoney as MoneyIcon, FilterList as FilterIcon, Assessment as SummaryIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import {
  tradesService, PurchaseInfo, PurchaseInfoPayload, Product, ProductCategory,
} from '../../services/tradesService';

const usePermissions = () => {
  const { user } = useAuth();
  const p = user?.role?.permissions?.[0];
  return {
    canCreate: p?.create_permission ?? false,
    canUpdate: p?.update_permission ?? false,
    canDelete: p?.delete_permission || user?.role?.role_name === 'DATA_MANAGER' || user?.is_superuser,
  };
};

const fmt = (v: string | number) =>
  Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const today = () => new Date().toISOString().split('T')[0];

// Helper function to convert DD-MM-YYYY to YYYY-MM-DD
const formatDateForInput = (dateStr: string | Date): string => {
  if (!dateStr) return '';
  
  let d: Date;
  
  if (typeof dateStr === 'string' && dateStr.match(/^\d{2}-\d{2}-\d{4}/)) {
    // Parse DD-MM-YYYY HH:mm:ss format
    const [datePart] = dateStr.split(' ');
    const [day, month, year] = datePart.split('-');
    // Return in YYYY-MM-DD format for input field
    return `${year}-${month}-${day}`;
  }
  
  // If it's already in YYYY-MM-DD format or a Date object
  d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ── Form ──────────────────────────────────────────────────────────────────────
interface FV {
  purchase_date: string; product: Product | null;
  quantity_pcs: string; purchase_rate: string; total_purchase_price: string; remarks: string;
}

const PurchaseFormDialog: React.FC<{
  open: boolean; editing: PurchaseInfo | null; products: Product[];
  onClose: () => void; onSaved: () => void;
}> = ({ open, editing, products, onClose, onSaved }) => {
  const [saving, setSaving]     = useState(false);
  const [apiError, setApiError] = useState('');

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FV>({
    defaultValues: { purchase_date: today(), product: null, quantity_pcs: '', purchase_rate: '', total_purchase_price: '', remarks: '' },
  });

  const qty  = watch('quantity_pcs');
  const rate = watch('purchase_rate');
  
  // Auto-calculate total
  useEffect(() => {
    const q = parseFloat(qty || '0');
    const r = parseFloat(rate || '0');
    if (q > 0 && r > 0) setValue('total_purchase_price', (q * r).toFixed(2));
  }, [qty, rate, setValue]);

  useEffect(() => {
    if (!open) return;
    setApiError('');
    if (editing) {
      const prod = products.find(p => p.product_id === editing.product) ?? null;
      // FIXED: Convert date to YYYY-MM-DD format for input field
      const formattedDate = formatDateForInput(editing.purchase_date);
      
      reset({
        purchase_date: formattedDate || today(),
        product: prod,
        quantity_pcs: String(editing.quantity_pcs),
        purchase_rate: editing.purchase_rate,
        total_purchase_price: editing.total_purchase_price,
        remarks: editing.remarks || '',
      });
    } else {
      reset({ purchase_date: today(), product: null, quantity_pcs: '', purchase_rate: '', total_purchase_price: '', remarks: '' });
    }
  }, [open, editing, products, reset]);

  const onSubmit = async (data: FV) => {
    if (!data.product) { setApiError('Please select a product.'); return; }
    setSaving(true); setApiError('');
    try {
      const payload: PurchaseInfoPayload = {
        purchase_date: data.purchase_date,
        product: data.product.product_id,
        quantity_pcs: parseInt(data.quantity_pcs),
        purchase_rate: parseFloat(data.purchase_rate),
        total_purchase_price: parseFloat(data.total_purchase_price),
        remarks: data.remarks.trim(),
      };
      if (editing) await tradesService.updatePurchase(editing.purchase_info_id, payload);
      else await tradesService.createPurchase(payload);
      onSaved(); onClose();
    } catch (e: any) {
      const errs = e?.data?.errors;
      setApiError(errs?.quantity_pcs?.[0] || errs?.purchase_rate?.[0] || e?.data?.message || 'Something went wrong.');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
      <Box sx={{ background: 'linear-gradient(135deg, #2196F3 0%, #1565C0 100%)', px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PurchaseIcon sx={{ color: 'white', fontSize: 22 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
            {editing ? 'Edit Purchase Entry' : 'New Purchase Entry'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white', p: 0.5 }}><CloseIcon /></IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {apiError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{apiError}</Alert>}
          <Stack spacing={2.5}>
          <Controller 
            name="purchase_date" 
            control={control} 
            rules={{ 
              required: 'Date is required',
              max: { value: new Date().toISOString().split('T')[0], message: 'Future date not allowed' }
            }}
            render={({ field }) => (
              <TextField 
                {...field} 
                fullWidth 
                label="Purchase Date *" 
                type="date" 
                error={!!errors.purchase_date}
                helperText={errors.purchase_date?.message}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split('T')[0] }} 
                InputProps={{ startAdornment: <InputAdornment position="start"><CalendarIcon fontSize="small" sx={{ color: '#2196F3' }} /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} 
              />
            )} 
          />

            <Controller name="product" control={control}
              render={({ field }) => (
                <Autocomplete
                  options={products.filter(p => p.is_active)}
                  getOptionLabel={o => `${o.product_name} (${o.unit_of_measure})`}
                  value={field.value}
                  onChange={(_, v) => field.onChange(v)}
                  renderInput={params => (
                    <TextField {...params} label="Product *" error={!!errors.product}
                      helperText={errors.product?.message}
                      InputProps={{ ...params.InputProps, startAdornment: <><InputAdornment position="start"><ProductIcon fontSize="small" sx={{ color: '#2196F3' }} /></InputAdornment>{params.InputProps.startAdornment}</> }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  )}
                />
              )} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller name="quantity_pcs" control={control}
                rules={{ required: 'Required', min: { value: 1, message: 'Must be > 0' }, pattern: { value: /^\d+$/, message: 'Whole number only' } }}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Quantity (Pcs) *" type="number" error={!!errors.quantity_pcs}
                    helperText={errors.quantity_pcs?.message}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )} />

              <Controller name="purchase_rate" control={control}
                rules={{ required: 'Required', min: { value: 0, message: 'Must be ≥ 0' } }}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Purchase Rate *" type="number"
                    inputProps={{ step: '0.01' }} error={!!errors.purchase_rate}
                    helperText={errors.purchase_rate?.message}
                    InputProps={{ startAdornment: <InputAdornment position="start">Tk. </InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )} />
            </Stack>

            <Controller name="total_purchase_price" control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Total Purchase Price"
                  type="number" inputProps={{ step: '0.01' }}
                  InputProps={{ startAdornment: <InputAdornment position="start">Tk. </InputAdornment> }}
                  helperText="Auto-calculated from Qty × Rate. You can override."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, background: alpha('#2196F3', 0.03) } }} />
              )} />

            <Controller name="remarks" control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Remarks" multiline rows={2}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={onClose} disabled={saving} variant="outlined"
            sx={{ borderRadius: 2, borderColor: alpha('#2196F3', 0.4), color: '#2196F3' }}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #2196F3 0%, #1565C0 100%)' }}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ── Delete Dialog ─────────────────────────────────────────────────────────────
const DeleteDialog: React.FC<{ open: boolean; id: number | null; onClose: () => void; onDeleted: () => void }> = ({ open, id, onClose, onDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const handleDelete = async () => {
    if (!id) return; setLoading(true); setError('');
    try { await tradesService.deletePurchase(id); onDeleted(); onClose(); }
    catch (e: any) { setError(e?.data?.message || 'Failed to delete.'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Box sx={{ width: 60, height: 60, borderRadius: '50%', background: alpha('#f44336', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <DeleteIcon sx={{ color: '#f44336', fontSize: 28 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} mb={1}>Delete Purchase Entry</Typography>
        <Typography variant="body2" color="text.secondary">Are you sure? This action cannot be undone.</Typography>
        {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading} sx={{ borderRadius: 2, minWidth: 100 }}>Cancel</Button>
        <Button onClick={handleDelete} variant="contained" color="error" disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          sx={{ borderRadius: 2, minWidth: 100 }}>{loading ? 'Deleting...' : 'Delete'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const PurchaseInfoPage: React.FC = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [rows, setRows]         = useState<PurchaseInfo[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [totals, setTotals]     = useState<Record<string, number | null>>({});
  const [loading, setLoading]   = useState(true);

  const [search, setSearch]       = useState('');
  const [dateFrom, setDateFrom]   = useState('');
  const [dateTo, setDateTo]       = useState('');

  const [formOpen, setFormOpen]   = useState(false);
  const [editing, setEditing]     = useState<PurchaseInfo | null>(null);
  const [delId, setDelId]         = useState<number | null>(null);

  

  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [filterProductId, setFilterProductId] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search)           params.search       = search;
      if (dateFrom)         params.date_from    = dateFrom;
      if (dateTo)           params.date_to      = dateTo;
      if (filterProductId)  params.product_id   = filterProductId;   
      if (filterCategoryId) params.category_id  = filterCategoryId;  
      const res = await tradesService.listPurchases(params);
      setRows(res.results);
      setTotals(res.totals || {});
    } catch { } finally { setLoading(false); }
  }, [search, dateFrom, dateTo, filterProductId, filterCategoryId]); 

  useEffect(() => {
  tradesService.listProducts({ is_active: true }).then(r => setProducts(r.results)).catch(() => {});
  tradesService.listCategories({ is_active: true }).then(r => setCategories(r.results)).catch(() => {}); 
}, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDate = (date: string | Date) => {
    let d: Date;
    
    if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}/)) {
      // Parse DD-MM-YYYY HH:mm:ss format
      const [datePart, timePart] = date.split(' ');
      const [day, month, year] = datePart.split('-');
      const timeString = timePart || '00:00:00';
      // Create date in YYYY-MM-DD format for proper parsing
      d = new Date(`${year}-${month}-${day}T${timeString}`);
    } else {
      d = new Date(date);
    }
    
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    
    return `${day}-${month}-${year}`;
  };

  const ACCENT = '#2196F3';

  return (
    <Fade in timeout={400}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography variant="h5" fontWeight={800}
              sx={{ background: `linear-gradient(135deg, ${ACCENT} 0%, #1565C0 100%)`,
                backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Purchase Information
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>Record and manage all purchase entries</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh"><IconButton onClick={fetchData} size="small"
              sx={{ border: `1px solid ${alpha(ACCENT, 0.3)}`, color: ACCENT }}>
              <RefreshIcon fontSize="small" /></IconButton></Tooltip>
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} size="small"
                onClick={() => { setEditing(null); setFormOpen(true); }}
                sx={{ borderRadius: 2, background: `linear-gradient(135deg, ${ACCENT} 0%, #1565C0 100%)`,
                  boxShadow: `0 4px 12px ${alpha(ACCENT, 0.35)}` }}>
                Add Purchase
              </Button>
            )}
          </Stack>
        </Box>

        {/* Summary Cards */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2.5 }}>
          {[
            { label: 'Total Records', value: rows.length, icon: <PurchaseIcon />, color: ACCENT },
            { label: 'Total Quantity', value: totals.total_qty ?? 0, icon: <ProductIcon />, color: '#4CAF50', suffix: ' Pcs' },
            { label: 'Total Amount', value: Number(totals.total_amount ?? 0), icon: <MoneyIcon />, color: '#FF9800', prefix: 'Tk. ', decimal: true },
          ].map(c => (
            <Card key={c.label} elevation={0} sx={{ flex: 1, p: 2, borderRadius: 3, border: `1px solid ${alpha(c.color, 0.2)}`,
              background: alpha(c.color, 0.04), display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: alpha(c.color, 0.12),
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.color, flexShrink: 0 }}>
                {c.icon}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>{c.label}</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: c.color, lineHeight: 1.2 }}>
                  {c.prefix}{c.decimal ? fmt(c.value) : c.value}{c.suffix}
                </Typography>
              </Box>
            </Card>
          ))}
        </Stack>

        {/* Filters */}
        <Card sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${alpha(ACCENT, 0.1)}` }} elevation={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" flexWrap="wrap">

            <TextField size="small" label="From" type="date" value={dateFrom}
              onChange={e => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 148, '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />

            <TextField size="small" label="To" type="date" value={dateTo}
              onChange={e => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 148, '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />

            <FormControl size="small" sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 6 } }}>
              <InputLabel>Product</InputLabel>
              <Select value={filterProductId} label="Product"
                onChange={e => setFilterProductId(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {products.map(p => (
                  <MenuItem key={p.product_id} value={String(p.product_id)}>{p.product_name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 6 } }}>
              <InputLabel>Category</InputLabel>
              <Select value={filterCategoryId} label="Category"
                onChange={e => setFilterCategoryId(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {categories.map(c => (
                  <MenuItem key={c.category_id} value={String(c.category_id)}>{c.category_name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button size="small" variant="outlined"
              onClick={() => {
                setSearch(''); setDateFrom(''); setDateTo('');
                setFilterProductId(''); setFilterCategoryId('');
              }}
              sx={{
                borderRadius: 6, borderColor: alpha(ACCENT, 0.5), color: ACCENT,
                fontWeight: 600, px: 2,
                '&:hover': { borderColor: ACCENT, background: alpha(ACCENT, 0.06) },
              }}>
              Clear
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {rows.length} records
            </Typography>
          </Stack>
        </Card>

        {/* Table */}
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(ACCENT, 0.1)}`, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: alpha(ACCENT, 0.04) }}>
                  {['#', 'Date', 'Product', 'Qty (Pcs)', 'Purchase Rate (Tk.)', 'Total (Tk.)', 'Remarks', 'Created By', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'text.secondary',
                      py: 1.5, borderBottom: `2px solid ${alpha(ACCENT, 0.12)}`, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={32} sx={{ color: ACCENT }} /></TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <PurchaseIcon sx={{ fontSize: 40, color: 'text.disabled', display: 'block', mx: 'auto', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No purchase entries found</Typography>
                  </TableCell></TableRow>
                ) : rows.map((r, idx) => (
                  <TableRow key={r.purchase_info_id} sx={{ '&:hover': { background: alpha(ACCENT, 0.03) } }}>
                    <TableCell sx={{ py: 1.25, color: 'text.secondary', fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {formatDate(r.purchase_date)}
                    </TableCell>
                    <TableCell sx={{ py: 1.25, fontSize: '0.875rem', maxWidth: 160 }}>
                      <Typography noWrap fontWeight={600} fontSize="inherit" title={r.product_detail?.product_name}>
                        {r.product_detail?.product_name ?? '—'}
                      </Typography>
                      {r.product_detail?.unit_of_measure && (
                        <Typography variant="caption" color="text.secondary">{r.product_detail.unit_of_measure}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 700, color: '#2196F3' }}>{r.quantity_pcs}</TableCell>
                    <TableCell sx={{ py: 1.25, textAlign: 'right', fontSize: '0.82rem' }}>{fmt(r.purchase_rate)}</TableCell>
                    <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 700, color: '#FF9800' }}>{fmt(r.total_purchase_price)}</TableCell>
                    <TableCell sx={{ py: 1.25, color: 'text.secondary', fontSize: '0.78rem', maxWidth: 120 }}>
                      <Typography noWrap variant="body2" fontSize="inherit">{r.remarks || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.25, color: 'text.secondary', fontSize: '0.78rem' }}>{r.created_by}</TableCell>
                    <TableCell sx={{ py: 1.25 }}>
                      <Stack direction="row" spacing={0.5}>
                        {canUpdate && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormOpen(true); }}
                          sx={{ color: ACCENT, '&:hover': { background: alpha(ACCENT, 0.1) } }}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                        {canDelete && <Tooltip title="Delete"><IconButton size="small" onClick={() => setDelId(r.purchase_info_id)}
                          sx={{ color: '#f44336', '&:hover': { background: alpha('#f44336', 0.1) } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                {rows.length > 0 && !loading && (
                  <TableRow sx={{ background: alpha(ACCENT, 0.06) }}>
                    <TableCell colSpan={3} sx={{ py: 1.25, fontWeight: 800, fontSize: '0.82rem', color: 'text.secondary' }}>TOTALS</TableCell>
                    <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#2196F3' }}>{totals.total_qty ?? 0}</TableCell>
                    <TableCell />
                    <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#FF9800' }}>{fmt(totals.total_amount ?? 0)}</TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <PurchaseFormDialog open={formOpen} editing={editing} products={products}
          onClose={() => setFormOpen(false)} onSaved={fetchData} />
        <DeleteDialog open={!!delId} id={delId} onClose={() => setDelId(null)} onDeleted={fetchData} />
      </Box>
    </Fade>
  );
};
export default PurchaseInfoPage;